import { createContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { MeResponse } from '@/lib/types';

export interface AuthState {
  user: MeResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- context is intentionally co-located with provider
export const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: api.me,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 60_000,
  });

  const value: AuthState = {
    user: data ?? null,
    loading: isLoading,
    error: (error as Error | null) ?? null,
    refetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
